import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ComputedAvailability, ExchangeService } from '../../_shared/data-access/exchange.service';

/**
 * FilterService is an injectable service in charge of managing price range filters for venue sections.
 * It provides functionality to set, update, and apply filters on sections based on their price range.
 * It uses BehaviorSubjects to manage and emit changes to the filter range values and Observable to allow subscribers
 * to react to changes.
 */
@Injectable({
  providedIn: 'root',
})
export class FilterService {
  protected exchangeService = inject(ExchangeService);
  public minPrice = Infinity;
  public maxPrice = 0;

  private readonly _minFilter = new BehaviorSubject<number>(this.minPrice);
  public readonly minFilter$ = this._minFilter.asObservable();
  private readonly _maxFilter = new BehaviorSubject<number>(this.maxPrice);
  public readonly maxFilter$ = this._maxFilter.asObservable();

  /**
   * Sets the minimum and maximum price range for sections based on the provided availability data.
   * Updates the filter values if new minimum or maximum prices are found.
   * @param availability Array of SectionModel containing price range data.
   */
  public setSectionPriceRange = (availability: ComputedAvailability[]) => {
    this.minPrice = Infinity;
    this.maxPrice = 0;
    availability.forEach((section: ComputedAvailability) => {
      const price = section.price;
      const isNewMinLower = price < this.minPrice && price >= 0;
      const isNewMaxHigher = price > this.maxPrice;

      if (isNewMinLower) {
        this.minPrice = price;
      }

      if (isNewMaxHigher) {
        this.maxPrice = price;
      }
    });

    this.updateMaxRange(this.maxPrice);
    this.updateMinRange(this.minPrice);
  };

  /**
   * Filters a section by checking if its price range falls within the current filter range.
   * @param section The section to filter.
   * @returns A boolean indicating if the section passes the filter.
   */
  public filterSectionByRange = (section: ComputedAvailability) => {
    return this._minFilter.getValue() <= section.price && section.price <= this._maxFilter.getValue();
  };
  public filterSectionByRangeWithoutDowngrades = (section: ComputedAvailability) => {
    return this._minFilter.getValue() <= section.price && section.price <= this._maxFilter.getValue();
  };

  /**
   * Updates the minimum price filter based on a value from an event, typically from a user input.
   * @param event The DOM event containing the new minimum value.
   */
  public updateMinRangeFromEvent = (event: Event) => {
    const value = parseInt((event.target as HTMLInputElement).value);
    this.updateMinRange(value);
  };

  /**
   * Updates the maximum price filter based on a value from an event, typically from a user input.
   * @param event The DOM event containing the new maximum value.
   */
  public updateMaxRangeFromEvent = (event: Event) => {
    const value = parseInt((event.target as HTMLInputElement).value);
    this.updateMaxRange(value);
  };

  /**
   * Updates the minimum price filter to a specified value.
   * @param value The new value for the minimum price filter.
   */
  private updateMinRange = (value: number) => {
    this._minFilter.next(value);
  };

  /**
   * Updates the maximum price filter to a specified value.
   * @param value The new value for the maximum price filter.
   */
  private updateMaxRange = (value: number) => {
    this._maxFilter.next(value);
  };
}
